import type { CSSProperties } from 'react';

export type BrandSite = 'armory' | 'warcraftlogs' | 'raiderio';

const BRAND_ICON_SRC: Record<BrandSite, string> = {
  armory: '/wowarmory.png',
  warcraftlogs: '/warcraftlog.png',
  raiderio: '/raiderio.svg',
};

/**
 * A real outbound-link brand mark (Armory/Warcraft Logs/Raider.IO) — always
 * paired with its own translated text label right next to it, so the icon
 * itself stays purely decorative (`alt=""`/`aria-hidden`) rather than
 * duplicating that text for assistive tech.
 *
 * Raider.IO's own asset is a wide wordmark+badge lockup, not a square icon
 * like the other two — its left square portion (the compass badge) is
 * cropped out here via a background-image trick so all three brand icons
 * read at the same small square size next to a text label.
 *
 * `size` is passed down as a CSS custom property rather than a `width`/
 * `height` style, so a call site's own stylesheet (e.g. the roster hero
 * card's responsive `clamp()` icon sizing) can still override it — an
 * inline `style` width/height would win over any stylesheet rule and
 * defeat that.
 */
export function BrandIcon({ site, size = 14 }: { site: BrandSite; size?: number }) {
  const style = { '--brand-icon-size': `${size}px` } as CSSProperties;
  if (site === 'raiderio') {
    return (
      <span
        aria-hidden="true"
        className="brand-icon brand-icon-raiderio"
        style={{ ...style, backgroundImage: `url(${BRAND_ICON_SRC.raiderio})` }}
      />
    );
  }
  return <img src={BRAND_ICON_SRC[site]} alt="" className="brand-icon" style={style} />;
}
